import { Copy, Link2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { API_BASE, apiFetch } from "../api";
import { db } from "../db/client";
import { fullDate } from "./format";

/*
 * Note ··· menu > Share: public, read-only links to this note (/s/<slug>), each optionally expiring,
 * each revocable. Needs a connection (links live only on the server).
 */

interface Share {
  slug: string;
  created: number;
  expires: number | null;
}

const EXPIRY: [string, number | null][] = [
  ["Never expires", null],
  ["1 day", 1],
  ["7 days", 7],
  ["30 days", 30],
];

export function shareUrl(slug: string) {
  return `${API_BASE || location.origin}/s/${slug}`;
}

export function ShareDialog({ noteId, title, onClose }: { noteId: string; title: string; onClose: () => void }) {
  const [shares, setShares] = useState<Share[] | null>(null);
  const [days, setDays] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await apiFetch(`/shares?note=${encodeURIComponent(noteId)}`);
      if (!r.ok) throw new Error();
      setShares(((await r.json()) as { shares: Share[] }).shares);
    } catch {
      setError("Sharing needs a connection to the server.");
    }
  };
  useEffect(() => void load(), [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (slug: string) => {
    await navigator.clipboard.writeText(shareUrl(slug));
    setCopied(slug);
    setTimeout(() => setCopied((c) => (c === slug ? null : c)), 1500);
  };

  const create = async () => {
    setBusy(true);
    setError("");
    try {
      await db.sync(); // the server must have the note before it can show it
      const r = await apiFetch("/shares", { method: "POST", body: JSON.stringify({ note_id: noteId, days }) });
      const j = (await r.json()) as Share & { error?: string };
      if (!r.ok) throw new Error(j.error ?? "Couldn't create the link.");
      await load();
      await copy(j.slug);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Couldn't create the link.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (slug: string) => {
    const r = await apiFetch(`/shares/${slug}`, { method: "DELETE" });
    if (r.ok) setShares((s) => s?.filter((x) => x.slug !== slug) ?? null);
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal share-dialog" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <h2>Share “{title || "Untitled"}”</h2>
        <p className="muted">Anyone with the link can read this note (not edit it). Turn a link off at any time.</p>
        {shares?.length ? (
          <ul className="share-list">
            {shares.map((s) => (
              <li key={s.slug}>
                <Link2 size={14} className="muted" />
                <span className="share-url" title={shareUrl(s.slug)}>
                  {shareUrl(s.slug)}
                </span>
                <span className="share-exp muted">{s.expires ? `until ${fullDate(s.expires)}` : "no expiry"}</span>
                <button className="btn small" onClick={() => void copy(s.slug)}>
                  <Copy size={12} /> {copied === s.slug ? "Copied" : "Copy"}
                </button>
                <button className="icon-btn" title="Turn off this link" aria-label="Turn off this link" onClick={() => void revoke(s.slug)}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        ) : shares ? (
          <p className="muted small">No links yet.</p>
        ) : null}
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-actions">
          <select className="input share-expiry" value={days ?? ""} onChange={(e) => setDays(e.target.value ? Number(e.target.value) : null)}>
            {EXPIRY.map(([label, d]) => (
              <option key={label} value={d ?? ""}>
                {label}
              </option>
            ))}
          </select>
          <span className="sketch-gap" />
          <button className="btn" onClick={onClose}>
            Done
          </button>
          <button className="btn primary" disabled={busy || !!(error && !shares)} onClick={() => void create()}>
            {busy ? "Creating…" : "Create link"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
